import { useState, useEffect, useRef, RefObject, memo } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import Link from '@/components/common/Link'
import Spinner from '@/components/common/SpinnerPage'
import LangLink from '@/components/common/LangLink'
import { TownResponse, AirportResponse } from '@/types/api/town-and-airports'
import { useTowns, usePandfTowns } from '@/service/apiHooks/parkingQueries'
import { useNavigationTracking } from '@/hooks/useNavigationTracking'
import { useMarket } from '@/utils/multi-markets/context'

import { getArrowIcon } from '@/constants/arrowAssets'
import { getNavigationIcon } from '@/constants/navigationAssets'

import {
  NavigationLinksContainer,
  ContentWrapper,
  DropdownWrapper,
  DropdownButton,
  DropdownContent,
  DropdownItem,
  HighlightedDropdownItem,
  NavigationItem,
  IconWrapper,
  LangContainer,
  LoadingOverlay,
  AirportCode
} from './NavigationLinks.styled'

interface NavigationLinksProps {
  onClose?: () => void
}

type DropdownRefType = {
  [key: string]: RefObject<HTMLDivElement>
}

const NavigationLinks = ({ onClose }: NavigationLinksProps): JSX.Element => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const { locale, market } = useMarket()
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { trackClick, trackOpen, trackClose } = useNavigationTracking()
  const dropdownRefs: DropdownRefType = {
    towns: useRef<HTMLDivElement>(null),
    airports: useRef<HTMLDivElement>(null)
  }

  const region = market
  const { data: townsData } = useTowns(locale, region)
  const { data: airportsData } = usePandfTowns(locale, region)
  const towns = townsData?.towns || []
  const airports = airportsData?.airports || []

  useEffect(() => {
    trackOpen()
    return () => {
      trackClose()
    }
  }, [trackOpen, trackClose])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown) {
        const dropdownElement = dropdownRefs[activeDropdown]?.current
        if (
          dropdownElement &&
          !dropdownElement.contains(event.target as Node)
        ) {
          setActiveDropdown(null)
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDropdown, dropdownRefs])

  const toggleDropdown = (key: 'towns' | 'airports'): void => {
    setActiveDropdown(prev => (prev === key ? null : key))
  }

  const handleNavigation = async (route: any, linkName: string) => {
    setIsLoading(true)
    try {
      trackClick(`menu_mobile_${linkName}`)
      await router.push(route)
    } finally {
      setIsLoading(false)
      onClose?.()
    }
  }

  const handleTownClick = (town?: TownResponse): void => {
    const route = {
      pathname: '/parking-search',
      query: {
        radius: 100000,
        ...(town?.lat && { lat: town.lat }),
        ...(town?.lon && { lon: town.lon }),
        ...(town?.name && { locationLabel: town.name })
      }
    }
    handleNavigation(route, `search_town_${town?.name || 'all'}`)
  }

  const handleAirportClick = (airport?: AirportResponse): void => {
    const route = {
      pathname: '/parking-search',
      query: {
        ...(airport?.lat && { lat: airport.lat }),
        ...(airport?.lon && { lon: airport.lon }),
        ...(airport?.name && {
          locationLabel: `${airport.code} - ${airport.cityName}`
        }),
        ...(airport?.cityUrlSlug && { cityUrlSlug: airport.cityUrlSlug }),
        service: 'PARK_AND_FLY',
        radius: 100000
      }
    }
    handleNavigation(route, `search_airport_${airport?.code || 'all'}`)
  }

  const getTownHref = (town?: TownResponse): string => {
    if (!town) {
      return '/parking-search?radius=100000'
    }
    const params = new URLSearchParams({
      radius: '100000',
      lat: town.lat.toString(),
      lon: town.lon.toString(),
      locationLabel: town.name
    })
    return `/parking-search?${params.toString()}`
  }

  const getAirportHref = (airport?: AirportResponse): string => {
    if (!airport) {
      return '/parking-search?service=PARK_AND_FLY&radius=100000'
    }
    const params = new URLSearchParams({
      lat: airport.lat.toString(),
      lon: airport.lon.toString(),
      locationLabel: `${airport.code} - ${airport.cityName}`,
      cityUrlSlug: airport.cityUrlSlug,
      service: 'PARK_AND_FLY',
      radius: '100000'
    })
    return `/parking-search?${params.toString()}`
  }

  return (
    <>
      <NavigationLinksContainer
        role="navigation"
        aria-label={t('navigation.mainMenu' as keyof typeof t)}
      >
        <ContentWrapper>
          <DropdownWrapper ref={dropdownRefs.towns}>
            <DropdownButton
              onClick={() => {
                toggleDropdown('towns')
                trackClick('menu_mobile_towns_dropdown')
              }}
              isOpen={activeDropdown === 'towns'}
              aria-expanded={activeDropdown === 'towns'}
              aria-controls="towns-dropdown"
            >
              {t('navigation.showAllTowns')}
              <IconWrapper isOpen={activeDropdown === 'towns'}>
                <img
                  src={getNavigationIcon('rectangle')}
                  alt=""
                  width={10}
                  height={10}
                />
              </IconWrapper>
            </DropdownButton>
            <DropdownContent
              isOpen={activeDropdown === 'towns'}
              id="towns-dropdown"
              role="menu"
              aria-labelledby="towns-dropdown-button"
            >
              <HighlightedDropdownItem
                key="all_towns"
                href={getTownHref()}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault()
                  handleTownClick()
                }}
                role="menuitem"
                tabIndex={activeDropdown === 'towns' ? 0 : -1}
              >
                {t('navigation.showAllTowns')}
                <img src={getArrowIcon('arrow')} alt="Arrow icon" />
              </HighlightedDropdownItem>
              {towns.map(town => (
                <DropdownItem
                  key={town.name}
                  href={getTownHref(town)}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    handleTownClick(town)
                  }}
                  role="menuitem"
                  tabIndex={activeDropdown === 'towns' ? 0 : -1}
                >
                  {town.name}
                </DropdownItem>
              ))}
            </DropdownContent>
          </DropdownWrapper>

          <DropdownWrapper ref={dropdownRefs.airports}>
            <DropdownButton
              onClick={() => {
                toggleDropdown('airports')
                trackClick('menu_mobile_airports_dropdown')
              }}
              isOpen={activeDropdown === 'airports'}
              aria-expanded={activeDropdown === 'airports'}
              aria-controls="airports-dropdown"
            >
              {t('navigation.searchFly')}
              <IconWrapper isOpen={activeDropdown === 'airports'}>
                <img
                  src={getNavigationIcon('rectangle')}
                  alt=""
                  width={16}
                  height={20}
                />
              </IconWrapper>
            </DropdownButton>
            <DropdownContent
              isOpen={activeDropdown === 'airports'}
              id="airports-dropdown"
              role="menu"
              aria-labelledby="airports-dropdown-button"
            >
              <HighlightedDropdownItem
                key="all_airports"
                href={getAirportHref()}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault()
                  handleAirportClick()
                }}
                role="menuitem"
                tabIndex={activeDropdown === 'airports' ? 0 : -1}
              >
                {t('navigation.showAllAirports')}
                <img src={getArrowIcon('arrow')} alt="Arrow icon" />
              </HighlightedDropdownItem>
              {airports.map(airport => (
                <DropdownItem
                  key={airport.code}
                  href={getAirportHref(airport)}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    handleAirportClick(airport)
                  }}
                  role="menuitem"
                  tabIndex={activeDropdown === 'airports' ? 0 : -1}
                >
                  <AirportCode>{airport.code}</AirportCode>
                  {t(airport.name as any)}
                </DropdownItem>
              ))}
            </DropdownContent>
          </DropdownWrapper>

          <NavigationItem
            href="/navipay"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              handleNavigation({ pathname: '/navipay' }, 'navipay_section')
            }}
          >
            {t('navigation.navipay')}
          </NavigationItem>

          <NavigationItem>
            <Link href="/faq" passHref prefetch={false}>
              <a
                onClick={() =>
                  handleNavigation({ pathname: '/faq' }, 'faq_section')
                }
                style={{ color: 'white' }}
              >
                {t('navigation.faq')}
              </a>
            </Link>
          </NavigationItem>

          <NavigationItem>
            <Link href="/contact" passHref prefetch={false}>
              <a
                onClick={() =>
                  handleNavigation({ pathname: '/contact' }, 'contact_section')
                }
                style={{ color: 'white' }}
              >
                {t('navigation.contact')}
              </a>
            </Link>
          </NavigationItem>
        </ContentWrapper>
      </NavigationLinksContainer>
      <LangContainer>
        <LangLink short={true} />
      </LangContainer>
      {isLoading && (
        <LoadingOverlay>
          <Spinner fullPage />
        </LoadingOverlay>
      )}
    </>
  )
}

export default memo(NavigationLinks)
